import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import {
  MarketplaceBankingSubProductTypeEnum,
  MarketplaceEntityTypeEnum,
} from '@repo/supergraph/marketplace/enums';
import { type DriverType } from '@repo/supergraph/marketplace/types';
import { useBestOfAwardsConfig } from '@repo/ui/BestOfAwards/Context';
import {
  BANNER_HEIGHT_FIXED_PX,
  BestOfAwardsRoundupBanner,
} from '@repo/ui/BestOfAwards/Roundup';
import { CPC_OFFERS_CONFIG, isWealthFrontModalOffer } from '@repo/ui/CPCComponents/config';
import { CPCTableCell } from '@repo/ui/CPCComponents/CPCTableCell';
import { CtaButton } from '@repo/ui/CtaButton';
import { theme } from '@repo/ui/NerdWalletThemeProvider';
import { NumericLayout } from '@repo/ui/StarRating/NumericLayout';

import { SingleStarLayout } from '../../../../StarRating/SingleStarLayout';
import { CdApysDriver } from '../../CdApysDriver';
import { type SummaryTableRowWithDrivers } from '../types';
import { DEFAULT_RATING_TOOLTIP } from '../utils';
import { BankInstitutionCell } from './BankInstitutionCell';
import { DriverCell } from './DriverCell';
import {
  SPOTLIGHT_BADGE_HEIGHT_PX,
  SPOTLIGHT_COMPACT_HEADING_MAX_LENGTH,
  SpotlightBadge,
} from './SpotlightBadge';

type Props = {
  index: number;
  row: SummaryTableRowWithDrivers;
  shouldApplyStylingUpdates?: boolean;
  suppressApyTermPrefix?: boolean;
  isImproveQualityTrafficExperimentActive?: boolean;
  shouldDisplayWealthFrontModal?: boolean;
};

export default function SummaryTableRow({
  index,
  row,
  shouldApplyStylingUpdates = false,
  suppressApyTermPrefix = false,
  isImproveQualityTrafficExperimentActive = false,
  shouldDisplayWealthFrontModal = false,
}: Props) {
  const {
    categoryName,
    oneYearApyEntity,
    threeYearApyEntity,
    fiveYearApyEntity,
    hideBestOfAwardsBanner = false,
    spotlightHeading,
    spotlightDetails,
    marketplaceEntity: {
      displayName,
      drivers,
      entityType,
      id,
      image,
      institution,
      monetizable: defaultMonetizable,
      name,
      offerForCta,
      product,
      productType,
      reviewLink,
      starRating,
      subProductType,
      ctaOverride,
      cta,
    },
  } = row;
  const monetizable = offerForCta?.monetizable ?? defaultMonetizable!;

  const offerId = offerForCta?.id ? offerForCta.id : id;
  const offerName = offerForCta?.name ? offerForCta.name : name;

  const analyticsPayload = {
    index,
    ...(institution?.id && { institutionId: institution.id }),
    ...(entityType === MarketplaceEntityTypeEnum.Institutions && { institutionId: id }),
    offerId,
    ...(product?.id && { productId: product.id }),
    productType,
    monetizable,
  };

  const isCDSubproductTypes = subProductType === MarketplaceBankingSubProductTypeEnum.CD;

  const { hasBestOfAward: hasBestOfAwardRaw, config: bestOfAwardsConfig } = useBestOfAwardsConfig(
    offerId,
    {
      institutionId: institution?.id,
      subProductType,
    },
  );

  const hasBestOfAward = hasBestOfAwardRaw && !hideBestOfAwardsBanner;
  const hasSpotlight = Boolean(spotlightHeading && spotlightDetails);
  const isCompactSpotlight =
    hasSpotlight && (spotlightHeading?.length ?? 0) <= SPOTLIGHT_COMPACT_HEADING_MAX_LENGTH;
  const hasTopBadge = hasSpotlight || hasBestOfAward;

  const badgeHeightPx = hasSpotlight ? SPOTLIGHT_BADGE_HEIGHT_PX : BANNER_HEIGHT_FIXED_PX;
  const cellPaddingTop = hasTopBadge
    ? `calc(${badgeHeightPx}px + ${theme.spacing(3)})`
    : theme.spacing(3);

  // Check if this offer should display a modal instead of a direct CTA
  const isWealthFrontOffer = isWealthFrontModalOffer(shouldDisplayWealthFrontModal, offerId);
  const isCpcModalOffer =
    isImproveQualityTrafficExperimentActive && CPC_OFFERS_CONFIG.includes(offerId);
  const shouldShowModal = isWealthFrontOffer || isCpcModalOffer;

  return (
    <AnalyticsSkuViewed
      source={SEGMENT_SKU_VIEWED_SOURCE}
      name={offerName}
      productType={productType}
      level="offer"
      {...(institution && {
        institutionId: institution.id,
        institutionName: institution.name,
      })}
      {...(entityType === MarketplaceEntityTypeEnum.Institutions && {
        institutionId: id,
        institutionName: name,
      })}
      {...(product && {
        productId: product.id,
        productName: product.name,
      })}
      offerId={offerId}
      offerName={offerName}
      monetizable={monetizable}
      impressionSection="summary_table_section"
      impressionPosition={index}
      additionalAttributes={{
        page_type: 'roundup',
      }}
    >
      <TableRow
        id={`summary-table-row-${index}`}
        sx={{
          '& td': {
            border: 'none',
            borderBottom: '1px solid',
            borderColor: 'neutral.lighter',
            verticalAlign: shouldApplyStylingUpdates ? 'middle' : 'top',
          },
          ...(shouldApplyStylingUpdates && {
            // Bank / institution (logo + name) stays top-aligned with tall rows.
            '& td:first-of-type': {
              verticalAlign: 'top',
            },
          }),
          scrollMarginBottom: '65px',
          position: hasTopBadge ? 'sticky' : undefined,
          '--cell-padding-top': cellPaddingTop,
        }}
      >
        <TableCell
          sx={{
            minWidth: {
              md: 190,
            },
            width: { xs: 190, md: drivers?.length ? 190 : 'auto' },
            paddingY: 3,
            paddingTop: `var(--cell-padding-top)`,
            paddingLeft: 0,
            paddingRight: 2,
            background: 'white',
            ...(isCompactSpotlight && { position: 'relative' }),
          }}
        >
          {hasSpotlight ? (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: 'fit-content',
                zIndex: 1,
                ...(isCompactSpotlight ? { minWidth: '100%' } : { maxWidth: '100%' }),
              }}
            >
              <SpotlightBadge
                heading={spotlightHeading!}
                variant={isCompactSpotlight ? 'compact' : 'full'}
              />
            </Box>
          ) : hasBestOfAward ? (
            <Box sx={{ position: 'absolute', left: 0, top: 0, width: '100%' }}>
              <BestOfAwardsRoundupBanner>{bestOfAwardsConfig.awardText}</BestOfAwardsRoundupBanner>
            </Box>
          ) : null}
          <BankInstitutionCell
            image={image}
            cta={ctaOverride ?? cta ?? null}
            analyticsPayload={analyticsPayload}
            displayName={displayName}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            shouldShowModal={shouldShowModal}
            modalVariant={isWealthFrontOffer ? 'wealthfront' : 'cpc'}
          />
        </TableCell>
        <TableCell
          sx={{
            paddingY: 3,
            paddingTop: `var(--cell-padding-top)`,
            paddingX: 2,
            width: 230,
            minWidth: { xs: 130 },
            ...(shouldApplyStylingUpdates && { textAlign: 'center' }),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              ...(shouldApplyStylingUpdates && { alignItems: 'center' }),
            }}
          >
            {shouldApplyStylingUpdates ? (
              <SingleStarLayout
                starRating={starRating}
                starRatingTooltip={DEFAULT_RATING_TOOLTIP}
                reviewLink={reviewLink}
              />
            ) : (
              <NumericLayout
                starRating={starRating}
                reviewLink={reviewLink}
                hideLabel
                iconSize={{ xs: 16, md: 17 }}
              />
            )}
            {!!categoryName && (
              <Typography variant="textSmall" sx={{ color: 'text.secondary' }}>
                {categoryName}
              </Typography>
            )}
          </Box>
        </TableCell>
        {drivers?.map((driver: DriverType, index: number) => {
          return (
            <TableCell
              key={index}
              sx={{
                paddingY: 3,
                paddingTop: `var(--cell-padding-top)`,
                paddingX: 2,
                width: 190,
                minWidth: { xs: 130 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...(shouldApplyStylingUpdates && { textAlign: 'center' }),
              }}
            >
              {driver?.id?.includes('apy') && isCDSubproductTypes ? (
                <CdApysDriver
                  driver={driver}
                  oneYearApyEntity={oneYearApyEntity}
                  threeYearApyEntity={threeYearApyEntity}
                  fiveYearApyEntity={fiveYearApyEntity}
                  suppressApyTermPrefix={suppressApyTermPrefix}
                />
              ) : (
                <DriverCell driver={driver} />
              )}
            </TableCell>
          );
        })}

        {shouldShowModal ? (
          <CPCTableCell
            cta={ctaOverride ?? cta ?? undefined}
            analyticsPayload={analyticsPayload}
            displayName={displayName}
            image={image}
            modalVariant={isWealthFrontOffer ? 'wealthfront' : 'cpc'}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
          />
        ) : (
          <TableCell
            sx={{
              width: 159,
              paddingY: 3,
              paddingTop: `var(--cell-padding-top)`,
              paddingX: 2,
              display: { xs: 'none', md: 'table-cell' },
              ...(shouldApplyStylingUpdates && { textAlign: 'center' }),
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                width: '159px',
                ...(shouldApplyStylingUpdates && { marginX: 'auto' }),
              }}
            >
              {(ctaOverride ?? cta) && (
                <CtaButton
                  cta={ctaOverride ?? cta}
                  analyticsPayload={analyticsPayload}
                  sx={{ height: { xs: 29, md: 45 } }}
                />
              )}
            </Box>
          </TableCell>
        )}
      </TableRow>
    </AnalyticsSkuViewed>
  );
}
